import { css, StyleDeclarationValue, StyleSheet } from 'aphrodite';
import { Avatar } from '@HoudiniUI/Avatar';
import { bs } from '../../styles/styles';
import { THEME } from '../Theme/theme';
import { ComboboxItem, ComboboxItemIndicator } from '.';

const styleSheet = StyleSheet.create({
	item: {
		display: 'grid',
		// avatar | text — `minmax(0, 1fr)` lets the text column shrink so its children can truncate.
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		gap: THEME.spacing.sm,
		alignItems: 'center',
	},
	avatar: {
		gridColumn: 1,
		// span both text rows so the avatar stays vertically centered next to title + description.
		gridRow: 'span 2',
		alignSelf: 'center',
		width: 28,
		height: 28,
	},
	text: {
		gridColumn: 2,
	},
});

// Avatar list item for a Combobox. Compose it from the parts below (avatar / title / description) so
// callers can style or augment any single part — e.g. wrapping a status badge into the description —
// rather than passing fixed `title`/`description` strings. `value`, `disabled`, `styles`, etc. pass
// through to the underlying ComboboxItem.
export function ComboboxAvatarItem({ styles = [], children, ...props }: React.ComponentProps<typeof ComboboxItem>) {
	return (
		<ComboboxItem styles={[styleSheet.item, ...styles]} {...props}>
			<ComboboxItemIndicator />
			{children}
		</ComboboxItem>
	);
}

// Avatar — left column, vertically centered against the title/description.
export function ComboboxAvatarItemAvatar({
	imageUrl,
	fallbackText,
	styles = [],
}: {
	imageUrl?: string;
	fallbackText?: string;
	styles?: StyleDeclarationValue[];
}) {
	return <Avatar imgSrc={imageUrl} fallbackText={fallbackText} styles={[styleSheet.avatar, ...styles]} smallText />;
}

// Title — bold primary line.
export function ComboboxAvatarItemTitle({
	children,
	styles = [],
}: {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}) {
	return <div className={css(styleSheet.text, bs.fontBold, bs.truncateText, ...styles)}>{children}</div>;
}

// Description — muted secondary line. Renders nothing when empty so an absent value doesn't leave a gap.
export function ComboboxAvatarItemDescription({
	children,
	styles = [],
}: {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}) {
	// Early-exit only works for scalar children (null / undefined / ''). Composite (JSX array)
	// children are the caller's responsibility — this guard won't catch them.
	if (children == null || children === '') {
		return null;
	}
	return (
		<div className={css(styleSheet.text, bs.textSm, bs.textDarkGrayFontColor, bs.truncateText, ...styles)}>
			{children}
		</div>
	);
}
